import { buildPhotoMoveOperation } from "../../photo-gallery-core.js";
import { getFunctionUrl } from "../config";
import type { AccessContext, DayReport, ReportAsset, ReportSummary } from "../types/api";
import { getAuthToken } from "./auth";
import { getSupabaseClient } from "./supabase";

const REQUEST_TIMEOUT_MS = 15_000;
const TEAM_PRESENCE_ORDER: Record<string, number> = {
  present: 0,
  partial: 1,
  absent: 2,
  unknown: 3,
};

type ApiErrorDetail = { code?: string; message?: string };
type ApiErrorBody = {
  error?: ApiErrorDetail | string;
  code?: string;
  message?: string;
};
type CommandBatchResult = {
  status?: string;
  code?: string;
  message?: string;
  report?: DayReport;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

function parseErrorBody(value: unknown): ApiErrorBody {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  const nested = record.error;
  const error = nested && typeof nested === "object" && !Array.isArray(nested)
    ? {
      code: typeof (nested as Record<string, unknown>).code === "string"
        ? (nested as Record<string, unknown>).code as string
        : undefined,
      message: typeof (nested as Record<string, unknown>).message === "string"
        ? (nested as Record<string, unknown>).message as string
        : undefined,
    }
    : typeof nested === "string"
    ? nested
    : undefined;
  return {
    error,
    code: typeof record.code === "string" ? record.code : undefined,
    message: typeof record.message === "string" ? record.message : undefined,
  };
}

async function apiCall<T>(
  method: string,
  path: string,
  body?: unknown,
  externalSignal?: AbortSignal,
): Promise<T> {
  const token = await getAuthToken();
  if (!token) {
    throw new ApiError(
      "Je sessie ontbreekt of is verlopen. Meld opnieuw aan.",
      401,
    );
  }

  const timeout = new AbortController();
  const timeoutId = globalThis.setTimeout(
    () => timeout.abort(),
    REQUEST_TIMEOUT_MS,
  );
  const signal = externalSignal ?? timeout.signal;

  try {
    const response = await fetch(getFunctionUrl("dbvdw-api", path), {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });

    const data: unknown = await response.json().catch(() => ({}));
    if (!response.ok) {
      const errorBody = parseErrorBody(data);
      const nestedError = typeof errorBody.error === "object" ? errorBody.error : undefined;
      const code = nestedError?.code || errorBody.code;
      const fallback = response.status === 401
        ? "Je sessie ontbreekt of is verlopen. Meld opnieuw aan."
        : response.status === 403
        ? "Je hebt geen toegang tot deze gegevens."
        : response.status === 409
        ? "De gegevens zijn intussen gewijzigd. Ververs en probeer opnieuw."
        : response.status >= 500
        ? "De server is tijdelijk niet beschikbaar."
        : `API-fout (${response.status}).`;
      const message = nestedError?.message ||
        (typeof errorBody.error === "string" ? errorBody.error : undefined) ||
        errorBody.message || fallback;
      throw new ApiError(message, response.status, code);
    }
    return data as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError("De server reageert niet binnen 15 seconden.", 408);
    }
    throw new ApiError("Netwerkfout bij het laden van de gegevens.", 0);
  } finally {
    clearTimeout(timeoutId);
  }
}

async function rpc<T>(name: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await getSupabaseClient().rpc(name, args);
  if (error) {
    throw new ApiError(error.message || `RPC ${name} mislukt`, 400, error.code);
  }
  return data as T;
}

function normalizeTeamMemberOrder(report: DayReport): DayReport {
  const teamMembers = [...(report.team_members ?? [])].sort((left, right) => {
    const leftRank = TEAM_PRESENCE_ORDER[String(left?.presence ?? "").toLowerCase()] ?? 99;
    const rightRank = TEAM_PRESENCE_ORDER[String(right?.presence ?? "").toLowerCase()] ?? 99;
    if (leftRank !== rightRank) return leftRank - rightRank;

    return String(left?.employee_name ?? left?.project_employee_id ?? "")
      .localeCompare(
        String(right?.employee_name ?? right?.project_employee_id ?? ""),
        "nl-NL",
        { sensitivity: "base" },
      );
  });

  return { ...report, team_members: teamMembers };
}

function assertCommandBatchAccepted(result: CommandBatchResult) {
  const status = String(result.status || "").toLowerCase();
  if (["rejected", "conflict", "needs_clarification"].includes(status)) {
    throw new ApiError(
      result.message || "De wijziging kon niet worden opgeslagen.",
      status === "conflict" ? 409 : 400,
      result.code,
    );
  }
}

export const api = {
  getAccessContext: () => apiCall<AccessContext>("GET", "/context"),
  listReports: (projectId: string, limit = 20) => {
    const params = new URLSearchParams({
      project_id: projectId,
      limit: String(limit),
    });
    return apiCall<ReportSummary[]>("GET", `/reports/list?${params}`);
  },
  getReport: async (reportId: string, signal?: AbortSignal) =>
    normalizeTeamMemberOrder(
      await apiCall<DayReport>(
        "GET",
        `/reports/${reportId}`,
        undefined,
        signal,
      ),
    ),
  moveReportAsset: async (
    reportId: string,
    revision: number,
    asset: ReportAsset,
    targetWorkAssignmentId: string,
  ) => {
    const move = buildPhotoMoveOperation(asset, targetWorkAssignmentId, revision);
    if (!move) {
      throw new ApiError(
        "Deze foto mist gegevens om hem naar een andere werkzaamheid te verplaatsen.",
        400,
      );
    }

    const result = await apiCall<CommandBatchResult>(
      "POST",
      `/reports/${reportId}/command-batches`,
      {
        expected_revision: revision,
        idempotency_key: move.operationId,
        operations: [move.operation],
      },
    );
    assertCommandBatchAccepted(result);

    if (result.report) return normalizeTeamMemberOrder(result.report);
    return normalizeTeamMemberOrder(
      await apiCall<DayReport>("GET", `/reports/${reportId}`),
    );
  },
  submitReport: async (reportId: string, revision: number) =>
    normalizeTeamMemberOrder(
      await apiCall<DayReport>("POST", `/reports/${reportId}/submit`, {
        expected_revision: revision,
      }),
    ),
  approveReport: async (reportId: string, revision: number) =>
    normalizeTeamMemberOrder(
      await apiCall<DayReport>("POST", `/reports/${reportId}/approve`, {
        expected_revision: revision,
      }),
    ),
  withdrawReport: async (reportId: string, revision: number, reason: string) =>
    normalizeTeamMemberOrder(
      await apiCall<DayReport>("POST", `/reports/${reportId}/withdraw`, {
        expected_revision: revision,
        reason,
      }),
    ),
  reopenApprovedReport: async (
    reportId: string,
    revision: number,
    reason: string,
  ) =>
    normalizeTeamMemberOrder(
      await apiCall<DayReport>("POST", `/reports/${reportId}/reopen`, {
        expected_revision: revision,
        reason,
      }),
    ),
  deleteReport: (reportId: string, revision: number, reason: string) =>
    rpc("delete_day_report", {
      target_day_report_id: reportId,
      expected_revision: revision,
      reason,
    }),
  purgeReport: (
    reportId: string,
    revision: number,
    reportDate: string,
    reason: string,
  ) =>
    rpc("purge_day_report", {
      target_day_report_id: reportId,
      expected_revision: revision,
      confirmation_report_date: reportDate,
      reason,
    }),
};
